import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ToolCallDisplay } from "./ToolCallDisplay.js";
import { ConfirmationDialog } from "./ConfirmationDialog.js";
import { QuestionsForm } from "./QuestionsForm.js";
import { PendingResolution } from "./PendingResolution.js";
/**
 * Custom markdown components — opens links in a new tab.
 */
const markdownComponents = {
    a({ href, children, ...props }) {
        return (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer", ...props, children: children }));
    },
};
/**
 * Pre-process AI-generated markdown to encode spaces inside link URLs.
 * Markdown parsers treat spaces as the end of a URL, so
 * `[text](https://host/path with spaces)` won't parse as a link.
 * This replaces spaces with %20 inside the URL portion of markdown links.
 */
function preprocessMarkdown(content) {
    return content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, text, url) => {
        const encoded = url.replace(/ /g, "%20");
        return `[${text}](${encoded})`;
    });
}
/**
 * Get HTML string from the current DOM selection.
 * Falls back to plain text if no range is available.
 */
function getSelectionHtml() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0)
        return null;
    const range = selection.getRangeAt(0);
    const fragment = range.cloneContents();
    const wrapper = document.createElement("div");
    wrapper.appendChild(fragment);
    return wrapper.innerHTML || null;
}
/**
 * Copy the rendered HTML content of a message-content element to the clipboard
 * as both text/html and text/plain so rich-text apps (Teams, Outlook, etc.)
 * preserve formatting on paste.
 */
async function copyMessageAsRichText(contentEl) {
    const html = contentEl.innerHTML;
    const plain = contentEl.innerText;
    try {
        await navigator.clipboard.write([
            new ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([plain], { type: "text/plain" }),
            }),
        ]);
        return true;
    }
    catch {
        // Fallback: plain text only
        await navigator.clipboard.writeText(plain);
        return true;
    }
}
function downloadMessageAsMarkdown(content, timestamp) {
    const blob = new Blob([`${content.trimEnd()}\n`], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const date = timestamp.toISOString().replace(/[:.]/g, "-");
    const contentSlug = content
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/<[^>]+>/g, " ")
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60)
        .replace(/-+$/g, "")
        .toLowerCase();
    link.href = url;
    link.download = `${contentSlug || "chat-response"}-${date}.md`;
    link.click();
    URL.revokeObjectURL(url);
}
// ─── ThinkingBlock (collapsible reasoning output) ────────────────
function ThinkingBlock({ content, isStreaming, }) {
    const [open, setOpen] = useState(false);
    const contentRef = useRef(null);
    // Auto-open while the model is still streaming thinking tokens
    useEffect(() => {
        if (isStreaming && content.length > 0) {
            setOpen(true);
        }
    }, [isStreaming, content]);
    // Auto-collapse when streaming finishes
    useEffect(() => {
        if (!isStreaming && content.length > 0) {
            setOpen(false);
        }
    }, [isStreaming]);
    // Auto-scroll thinking content to bottom as new tokens arrive
    useEffect(() => {
        if (open && contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
    }, [content, open]);
    return (_jsxs("details", { className: "thinking-block", open: open || undefined, children: [_jsxs("summary", { className: "thinking-summary", onClick: (e) => {
                    e.preventDefault();
                    setOpen((prev) => !prev);
                }, children: ["\uD83D\uDCAD Thinking", isStreaming && content.length > 0 ? "…" : ""] }), open && (_jsxs("div", { className: "thinking-content", ref: contentRef, children: [_jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: markdownComponents, children: preprocessMarkdown(content) }), isStreaming && _jsx("span", { className: "cursor-blink", children: "\u258A" })] }))] }));
}
export function MessageList({ messages, runStatus, onResolve }) {
    const bottomRef = useRef(null);
    const listRef = useRef(null);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const prevLengthRef = useRef(messages.length);
    // Auto-scroll to bottom when new messages arrive or the list length changes
    // (covers: user sends a message, assistant bubble appears, streaming updates)
    useEffect(() => {
        const el = listRef.current;
        if (!el)
            return;
        const isNewMessage = messages.length > prevLengthRef.current;
        prevLengthRef.current = messages.length;
        // Always scroll on new messages; otherwise only if near bottom
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        if (isNewMessage || nearBottom) {
            // Use requestAnimationFrame to wait for DOM render
            requestAnimationFrame(() => {
                bottomRef.current?.scrollIntoView({ behavior: "smooth" });
            });
        }
    }, [messages, messages.length]);
    // Track scroll position to show/hide the scroll-to-bottom button
    useEffect(() => {
        const el = listRef.current;
        if (!el)
            return;
        const onScroll = () => {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            setShowScrollBtn(distanceFromBottom > 120);
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, []);
    const scrollToBottom = useCallback(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);
    // Memoize remarkPlugins array to avoid re-creating on every render
    const remarkPlugins = useMemo(() => [remarkGfm], []);
    return (_jsxs("div", { className: "message-list", ref: listRef, children: [messages.length === 0 && (_jsx("div", { className: "empty-state", children: _jsx("p", { children: "Start a conversation by typing a message below." }) })), messages.map((msg) => (_jsx(MessageBubble, { msg: msg, runStatus: runStatus, onResolve: onResolve, remarkPlugins: remarkPlugins }, msg.id))), _jsx("div", { ref: bottomRef }), showScrollBtn && (_jsx("button", { className: "btn-scroll-bottom", onClick: scrollToBottom, type: "button", "aria-label": "Scroll to bottom", children: "\u2193" }))] }));
}
// ─── MessageBubble (individual message with copy support) ────────
function MessageBubble({ msg, runStatus, onResolve, remarkPlugins, }) {
    const showRunStatus = msg.role === "assistant" &&
        msg.isStreaming &&
        !msg.content &&
        !!runStatus &&
        !["thread_ready", "completed"].includes(runStatus);
    const contentRef = useRef(null);
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback((e) => {
        // Preserve HTML formatting in the clipboard so rich-text
        // targets (Teams, Outlook, etc.) keep tables, bold, etc.
        const html = getSelectionHtml();
        const plain = window.getSelection()?.toString();
        if (html && plain) {
            e.clipboardData.setData("text/html", html);
            e.clipboardData.setData("text/plain", plain.trimEnd());
            e.preventDefault();
        }
    }, []);
    const handleCopyButton = useCallback(async () => {
        if (!contentRef.current)
            return;
        const ok = await copyMessageAsRichText(contentRef.current);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }
    }, []);
    return (_jsxs("div", { className: `message message--${msg.role}`, onCopy: handleCopy, children: [msg.role !== "user" && (_jsx("div", { className: "message-header", children: _jsx("div", { className: "message-role", children: msg.role === "assistant"
                        ? "🤖 Assistant"
                        : "⚙️ System" }) })), msg.toolCalls && msg.toolCalls.length > 0 && (_jsx(ToolCallDisplay, { toolCalls: msg.toolCalls, isStreaming: msg.isStreaming })), msg.thinking && _jsx(ThinkingBlock, { content: msg.thinking, isStreaming: msg.isStreaming }), _jsxs("div", { className: "message-content", ref: contentRef, children: [msg.role === "assistant" ? (_jsx(ReactMarkdown, { remarkPlugins: remarkPlugins, components: markdownComponents, children: preprocessMarkdown(msg.content) })) : (msg.content), msg.isStreaming && _jsx("span", { className: "cursor-blink", children: "\u258A" }), showRunStatus && (_jsx("span", { className: "message-status", children: runStatus.replace(/_/g, " ") }))] }), (() => {
                const hasConfirm = (msg.pendingConfirmation?.pendingToolCalls.length ?? 0) > 0;
                const hasInput = (msg.pendingInput?.requests.length ?? 0) > 0;
                if (!hasConfirm && !hasInput)
                    return null;
                const runId = msg.pendingConfirmation?.runId ?? msg.pendingInput?.runId ?? "";
                if (hasConfirm && hasInput) {
                    return (_jsx(PendingResolution, { pendingConfirmation: msg.pendingConfirmation, pendingInput: msg.pendingInput, onResolve: (approvals, answers) => onResolve(msg.id, runId, approvals, answers) }));
                }
                if (hasConfirm) {
                    return (_jsx(ConfirmationDialog, { pendingToolCalls: msg.pendingConfirmation.pendingToolCalls, onConfirm: (approvals) => onResolve(msg.id, runId, approvals) }));
                }
                return (_jsx(QuestionsForm, { requests: msg.pendingInput.requests, onRespond: (answers) => onResolve(msg.id, runId, {}, answers) }));
            })(), msg.authRequired && (_jsx("div", { className: "auth-required", children: _jsxs("a", { href: msg.authRequired.authorizeUrl, target: "_blank", rel: "noopener noreferrer", className: "auth-link", children: ["\uD83D\uDD17 Connect to ", msg.authRequired.provider] }) })), _jsxs("div", { className: "message-footer", children: [msg.role === "assistant" && msg.content && !msg.isStreaming && (_jsxs("div", { className: "message-actions", children: [_jsx("button", { "aria-label": "Copy response", className: "message-action btn-copy", onClick: handleCopyButton, title: "Copy response", type: "button", children: copied ? "✓" : "⧉" }), _jsx("button", { "aria-label": "Download response as Markdown", className: "message-action btn-download", onClick: () => downloadMessageAsMarkdown(msg.content, msg.timestamp), title: "Download response as Markdown", type: "button", children: "\u2913" })] })), _jsx("span", { className: "message-time", children: msg.timestamp.toLocaleTimeString() })] })] }));
}
//# sourceMappingURL=MessageList.js.map